/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/langflowRun.ts
 * PURPOSE: Langflow v1 run API — JSON extract + SSE token streaming for browser fetch.
 * RECENT: 2026-04-27 — [2.2.703] **`POST /api/v1/platform/langflow/run/{id}`** via gateway — ops **`docs/assistant/PERPLEXITY_ASSISTANT_LANGFLOW.md`**. 2026-04-26 — **`describeStreamActivity`** for Langflow SSE `add_message` / tool hints; **`onStreamActivity`** callback.
 */

import { getApiBaseUrl, getAuthHeaders } from '@shared/lib/frontend/api/config';

export interface LangflowRunBody {
  input_value: string;
  input_type: 'chat';
  output_type: 'chat';
  session_id: string;
  tweaks: Record<string, unknown>;
}

/** Direct browser → Langflow, or authenticated proxy via API gateway. */
export type LangflowRunTransport =
  | { kind: 'direct'; baseUrl: string; apiKey?: string }
  | { kind: 'platform'; apiBaseUrl?: string };

export function extractAssistantText(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const root = data as Record<string, unknown>;
  const outs = root.outputs;
  if (!Array.isArray(outs) || outs.length === 0) return '';
  const first = outs[0] as Record<string, unknown>;
  const inner = first.outputs;
  if (!Array.isArray(inner) || inner.length === 0) return '';
  const block = inner[0] as Record<string, unknown>;
  const results = block.results as Record<string, unknown> | undefined;
  const message = results?.message as Record<string, unknown> | undefined;
  const text = message?.text;
  if (typeof text === 'string' && text.trim()) return text;
  const messages = results?.messages as Record<string, unknown> | undefined;
  const innerMsg = messages?.message as Record<string, unknown> | undefined;
  const alt = innerMsg?.text;
  if (typeof alt === 'string' && alt.trim()) return alt;
  try {
    return JSON.stringify(data);
  } catch {
    return 'Unable to parse response.';
  }
}

const MAX_ACTIVITY_LABEL = 72;

function truncateActivity(s: string, max = MAX_ACTIVITY_LABEL): string {
  const t = s.trim().replace(/\s+/g, ' ');
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/**
 * Human-readable status from Langflow SSE JSON (`event` + `data`), e.g. `add_message` with sender / source / tools.
 * Returns null for high-frequency `token` events and unknown shapes.
 */
export function describeStreamActivity(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const ev = typeof o.event === 'string' ? o.event.trim() : '';

  if (ev === 'token' || ev === 'chunk') return null;
  if (ev === 'end' || ev === 'complete') return null;

  if (ev === 'add_message') {
    const d = o.data;
    if (!d || typeof d !== 'object') return 'Working…';
    const data = d as Record<string, unknown>;

    const blocks = data.content_blocks;
    if (Array.isArray(blocks) && blocks.length > 0) {
      const last = blocks[blocks.length - 1];
      if (last && typeof last === 'object') {
        const b = last as Record<string, unknown>;
        const typ = typeof b.type === 'string' ? b.type : '';
        if (typ === 'tool_use' || typ === 'tool_call') {
          const name = typeof b.name === 'string' ? b.name.trim() : '';
          if (name) return truncateActivity(`Using tool · ${name}`);
        }
        if (typ && typ !== 'text' && typ !== 'message') {
          return truncateActivity(typ.replace(/_/g, ' '));
        }
      }
    }

    const props = data.properties;
    let sourceName = '';
    if (props && typeof props === 'object') {
      const src = (props as Record<string, unknown>).source;
      if (src && typeof src === 'object') {
        const dn = (src as Record<string, unknown>).display_name;
        if (typeof dn === 'string' && dn.trim()) sourceName = dn.trim();
      }
    }

    const senderName =
      typeof data.sender_name === 'string' && data.sender_name.trim()
        ? data.sender_name.trim()
        : typeof data.sender === 'string' && data.sender.trim()
          ? data.sender.trim()
          : '';

    const textPreview =
      typeof data.text === 'string' && data.text.trim().length > 0
        ? truncateActivity(data.text, 48)
        : '';

    if (senderName && sourceName) return truncateActivity(`${senderName} · ${sourceName}`);
    if (senderName && textPreview) return truncateActivity(`${senderName} · ${textPreview}`);
    if (senderName) return truncateActivity(senderName);
    if (sourceName) return truncateActivity(sourceName);
    return 'Working…';
  }

  if (ev) return truncateActivity(ev.replace(/_/g, ' '));
  return null;
}

/** Pull a token / text fragment from Langflow SSE `data:` JSON (shape varies by version). */
function extractStreamDelta(obj: unknown): string {
  if (!obj || typeof obj !== 'object') return '';
  const o = obj as Record<string, unknown>;

  if (typeof o.chunk === 'string') return o.chunk;
  if (typeof o.text === 'string') return o.text;

  const data = o.data;
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (typeof d.chunk === 'string') return d.chunk;
    if (typeof d.text === 'string') return d.text;
    if (typeof d.delta === 'string') return d.delta;
  }

  const msg = o.message;
  if (msg && typeof msg === 'object') {
    const t = (msg as Record<string, unknown>).text;
    if (typeof t === 'string') return t;
  }

  return '';
}

async function readEventStream(
  response: Response,
  onDelta: (piece: string) => void,
  signal: AbortSignal | undefined,
  onParsedEvent: ((parsed: unknown) => void) | undefined
): Promise<string> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Langflow returned no response body for stream.');

  const decoder = new TextDecoder();
  let carry = '';
  let out = '';

  let streamDone = false;
  while (!streamDone) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const { done, value } = await reader.read();
    if (done) {
      streamDone = true;
      break;
    }
    carry += decoder.decode(value, { stream: true });

    const blocks = carry.split(/\r?\n\r?\n/);
    carry = blocks.pop() ?? '';

    for (const block of blocks) {
      const lines = block.split(/\r?\n/);
      const dataPayloads: string[] = [];
      for (const line of lines) {
        if (line.startsWith('data:')) {
          dataPayloads.push(line.slice(5).trim());
        } else if (line.startsWith('data :')) {
          dataPayloads.push(line.slice(6).trim());
        }
      }
      const merged = dataPayloads.join('');
      if (!merged || merged === '[DONE]') continue;
      try {
        const parsed = JSON.parse(merged) as unknown;
        onParsedEvent?.(parsed);
        const piece = extractStreamDelta(parsed);
        if (piece) {
          out += piece;
          onDelta(piece);
        }
      } catch {
        // Non-JSON data line — ignore
      }
    }
  }

  for (const line of carry.split(/\r?\n/)) {
    const t = line.startsWith('data:') ? line.slice(5).trim() : line.trim();
    if (!t || t === '[DONE]') continue;
    try {
      const parsed = JSON.parse(t) as unknown;
      onParsedEvent?.(parsed);
      const piece = extractStreamDelta(parsed);
      if (piece) {
        out += piece;
        onDelta(piece);
      }
    } catch {
      /* ignore trailing noise */
    }
  }

  return out;
}

function buildRunUrl(transport: LangflowRunTransport, flowId: string, stream: boolean): string {
  const query = stream ? 'stream=true' : 'stream=false';
  const enc = encodeURIComponent(flowId);
  if (transport.kind === 'platform') {
    const base = (transport.apiBaseUrl ?? getApiBaseUrl()).replace(/\/$/, '');
    return `${base}/api/v1/platform/langflow/run/${enc}?${query}`;
  }
  const base = transport.baseUrl.replace(/\/$/, '');
  return `${base}/api/v1/run/${enc}?${query}`;
}

function buildRunHeaders(transport: LangflowRunTransport, stream: boolean): Record<string, string> {
  const accept = stream
    ? 'text/event-stream, application/json;q=0.9, */*;q=0.8'
    : 'application/json';
  if (transport.kind === 'platform') {
    return {
      ...getAuthHeaders(),
      Accept: accept,
    };
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: accept,
  };
  if (transport.apiKey) headers['x-api-key'] = transport.apiKey;
  return headers;
}

export async function runLangflowChat(opts: {
  transport: LangflowRunTransport;
  flowId: string;
  body: LangflowRunBody;
  stream: boolean;
  signal?: AbortSignal;
  onStreamAccumulated?: (fullSoFar: string) => void;
  /** Fired on Langflow SSE frames that carry workflow metadata (not every token). */
  onStreamActivity?: (label: string) => void;
}): Promise<string> {
  const { transport, flowId, body, stream, signal, onStreamAccumulated, onStreamActivity } = opts;
  const url = buildRunUrl(transport, flowId, stream);
  const headers = buildRunHeaders(transport, stream);

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  const ct = res.headers.get('content-type') ?? '';

  if (!res.ok) {
    const rawText = await res.text();
    let detail = rawText || res.statusText;
    try {
      const err = rawText ? (JSON.parse(rawText) as Record<string, unknown>) : {};
      if (typeof err.detail === 'string') detail = err.detail;
      else if (Array.isArray(err.detail)) detail = JSON.stringify(err.detail);
    } catch {
      /* keep raw */
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }

  if (stream && res.body && ct.includes('text/event-stream')) {
    let acc = '';
    const streamed = await readEventStream(
      res,
      (piece) => {
        acc += piece;
        onStreamAccumulated?.(acc);
      },
      signal,
      onStreamActivity
        ? (parsed) => {
            const label = describeStreamActivity(parsed);
            if (label) onStreamActivity(label);
          }
        : undefined
    );
    const text = streamed || acc;
    if (text.trim()) return text;
    return '';
  }

  const rawText = await res.text();
  let data: unknown;
  try {
    data = rawText ? JSON.parse(rawText) : {};
  } catch {
    data = { parse_error: rawText };
  }
  return extractAssistantText(data);
}
